import React, { useState, useCallback, useEffect } from "react";
import {
    Box, Flex, Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalCloseButton, ModalBody, ModalFooter, HStack, VStack, Image, Slider,
    SliderTrack, SliderFilledTrack, SliderThumb, Checkbox, Select, useToast,
    Badge, Spinner, Alert, AlertIcon, Divider,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import Cropper from "react-easy-crop";
import { editarImagem, restaurarImagemOriginal } from "../../services/api";
import { atualizarCacheImagem } from "../ProdutoImagem";

const MODELOS = [
    { valor: "u2net", texto: "Padrão (u2net)" },
    { valor: "isnet-general-use", texto: "Alternativo (isnet)" },
];

const EditorImagemModal = ({ isOpen, onClose, imagem, onSalvo }) => {
    const toast = useToast();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [areaRecorte, setAreaRecorte] = useState(null);
    const [rotacao, setRotacao] = useState(0);
    const [espelho, setEspelho] = useState(null);
    const [removerFundo, setRemoverFundo] = useState(false);
    const [modelo, setModelo] = useState("u2net");
    const [previa, setPrevia] = useState(null);
    const [alerta, setAlerta] = useState(null);
    const [gerando, setGerando] = useState(false);
    const [salvando, setSalvando] = useState(false);

    const urlBase = imagem?.url_original || imagem?.url;
    const partiuDoOriginal = !!imagem?.url_original;

    useEffect(() => {
        if (!isOpen) return;
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setAreaRecorte(null);
        setRotacao(0);
        setEspelho(null);
        setRemoverFundo(false);
        setModelo("u2net");
        setPrevia(null);
        setAlerta(null);
    }, [isOpen, imagem?.id]);

    const aoTerminarRecorte = useCallback((_area, areaEmPixels) => {
        setAreaRecorte(areaEmPixels);
    }, []);

    const montarPayload = (salvar) => ({
        crop: areaRecorte
            ? {
                x: Math.max(0, Math.round(areaRecorte.x)),
                y: Math.max(0, Math.round(areaRecorte.y)),
                largura: Math.round(areaRecorte.width),
                altura: Math.round(areaRecorte.height),
            }
            : null,
        rotacao,
        espelho,
        remover_fundo: removerFundo,
        modelo: removerFundo ? modelo : null,
        salvar,
    });

    const gerarPrevia = async () => {
        setGerando(true);
        setAlerta(null);
        try {
            const res = await editarImagem(imagem.id, montarPayload(false));
            if (res?.error) {
                toast({ title: res.error, status: "error", isClosable: true });
                return;
            }
            setPrevia(res.preview);
            setAlerta(res.alerta || null);
        } finally {
            setGerando(false);
        }
    };

    const salvar = async () => {
        setSalvando(true);
        try {
            const res = await editarImagem(imagem.id, montarPayload(true));
            if (res?.error) {
                toast({ title: res.error, status: "error", isClosable: true });
                return;
            }
            atualizarCacheImagem(imagem.produto_id, null);
            toast({ title: "Imagem editada e salva.", status: "success", duration: 2500, isClosable: true });
            if (onSalvo) onSalvo(res, { removeuFundo: removerFundo });
            onClose();
        } finally {
            setSalvando(false);
        }
    };

    const restaurar = async () => {
        setSalvando(true);
        try {
            const res = await restaurarImagemOriginal(imagem.id);
            if (res?.error) {
                toast({ title: res.error, status: "error", isClosable: true });
                return;
            }
            atualizarCacheImagem(imagem.produto_id, null);
            toast({ title: "Foto original restaurada.", status: "success", duration: 2500, isClosable: true });
            if (onSalvo) onSalvo(res, { removeuFundo: false });
            onClose();
        } finally {
            setSalvando(false);
        }
    };

    if (!imagem) return null;

    const ocupado = gerando || salvando;

    return (
        <Modal isOpen={isOpen} onClose={ocupado ? () => {} : onClose} size={{ base: "full", md: "5xl" }} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md">
                    Editar imagem #{imagem.id}{" "}
                    <Badge colorScheme={partiuDoOriginal ? "purple" : "gray"} ml={2}>
                        {partiuDoOriginal ? "partindo da foto original" : "partindo da foto publicada"}
                    </Badge>
                </ModalHeader>
                {!ocupado && <ModalCloseButton />}

                <ModalBody>
                    <Flex direction={{ base: "column", lg: "row" }} gap={4} align="stretch">
                        <Box flex="1" minW={0}>
                            <Text fontSize="sm" fontWeight="bold" mb={2}>Recorte</Text>
                            <Box position="relative" h={{ base: "300px", md: "420px" }} bg="gray.900" borderRadius="md" overflow="hidden">
                                <Cropper
                                    image={urlBase}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={undefined}
                                    restrictPosition={false}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={aoTerminarRecorte}
                                />
                            </Box>

                            <HStack mt={3} spacing={3}>
                                <Text fontSize="xs" color="gray.600" minW="38px">Zoom</Text>
                                <Slider value={zoom} min={1} max={4} step={0.05} onChange={setZoom} flex="1">
                                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                                    <SliderThumb />
                                </Slider>
                            </HStack>

                            <HStack mt={3} spacing={2} flexWrap="wrap">
                                <Button size="sm" leftIcon={<RepeatIcon />} onClick={() => setRotacao((r) => (r + 90) % 360)}>
                                    Girar 90°
                                </Button>
                                <Button size="sm" variant={espelho === "h" ? "solid" : "outline"} colorScheme="purple"
                                    onClick={() => setEspelho(espelho === "h" ? null : "h")}>
                                    Espelhar ↔
                                </Button>
                                <Button size="sm" variant={espelho === "v" ? "solid" : "outline"} colorScheme="purple"
                                    onClick={() => setEspelho(espelho === "v" ? null : "v")}>
                                    Espelhar ↕
                                </Button>
                                {rotacao > 0 && <Badge colorScheme="blue">giro {rotacao}°</Badge>}
                            </HStack>

                            <Divider my={3} />

                            <VStack align="stretch" spacing={2}>
                                <Checkbox isChecked={removerFundo} onChange={(e) => setRemoverFundo(e.target.checked)} colorScheme="purple">
                                    Remover o fundo
                                </Checkbox>
                                {removerFundo && (
                                    <Select size="sm" value={modelo} onChange={(e) => setModelo(e.target.value)} maxW="260px">
                                        {MODELOS.map((m) => <option key={m.valor} value={m.valor}>{m.texto}</option>)}
                                    </Select>
                                )}
                                <Text fontSize="xs" color="gray.500">
                                    A ordem é recorte, giro, espelho e por último a remoção do fundo. Recortar em volta da peça costuma
                                    melhorar bastante o resultado.
                                </Text>
                            </VStack>
                        </Box>

                        <Box flex="1" minW={0}>
                            <Text fontSize="sm" fontWeight="bold" mb={2}>Prévia</Text>
                            <Flex h={{ base: "300px", md: "420px" }} align="center" justify="center"
                                bg="gray.50" borderRadius="md" border="1px dashed" borderColor="gray.300">
                                {gerando ? (
                                    <VStack><Spinner /><Text fontSize="xs" color="gray.600">Processando…</Text></VStack>
                                ) : previa ? (
                                    <Image src={previa} maxH="100%" maxW="100%" objectFit="contain" />
                                ) : (
                                    <Text fontSize="sm" color="gray.500" px={4} textAlign="center">
                                        Ajuste o recorte e clique em Gerar prévia.
                                    </Text>
                                )}
                            </Flex>

                            {alerta && (
                                <Alert status="warning" mt={3} borderRadius="md" fontSize="xs">
                                    <AlertIcon />{alerta}
                                </Alert>
                            )}
                        </Box>
                    </Flex>
                </ModalBody>

                <ModalFooter>
                    <Flex w="100%" gap={2} flexWrap="wrap" justify="space-between">
                        <Button size="sm" variant="outline" colorScheme="orange" onClick={restaurar}
                            isDisabled={!partiuDoOriginal || ocupado}>
                            Voltar para a foto original
                        </Button>
                        <HStack spacing={2}>
                            <Button size="sm" variant="ghost" onClick={onClose} isDisabled={ocupado}>Cancelar</Button>
                            <Button size="sm" onClick={gerarPrevia} isLoading={gerando} isDisabled={salvando}>Gerar prévia</Button>
                            <Button size="sm" colorScheme="purple" onClick={salvar} isLoading={salvando} isDisabled={gerando}>
                                Salvar
                            </Button>
                        </HStack>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EditorImagemModal;
